/* GMU collection (T4): filters, sort and infinite scroll on top of a plain GET form + links.
   Every change fetches the same section via the Section Rendering API and swaps the [data-part]
   regions; the URL is kept in sync with history.replaceState. Loaded pages + scroll are saved in
   history.state so Back from a product restores the same list. No library. */
(function () {
  if (customElements.get('gmu-collection')) return;

  const MQ = window.matchMedia('(max-width:860px)');
  const SIDE_KEY = 'gmu-col-filters-open'; /* T14: sidebar hidden by default, '1' = visitor opened it */

  const withSection = (href, sid) => {
    const u = new URL(href, location.origin);
    u.searchParams.set('section_id', sid);
    return u.toString();
  };
  const clean = (href) => {
    const u = new URL(href, location.origin);
    u.searchParams.delete('section_id');
    return u.pathname + (u.search ? u.search : '');
  };

  class GmuCollection extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.section;
      this.side = this.querySelector('[data-gmu-side]');
      this.toggleBtn = this.querySelector('[data-gmu-toggle]');
      this.pages = 1;
      this.seq = 0;
      this.moreP = null;

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-gmu-form]')) this.applyForm();
      });
      this.addEventListener('submit', (e) => {
        if (!e.target.matches('[data-gmu-form]')) return;
        e.preventDefault();
        this.applyForm();
      });
      this.addEventListener('keydown', (e) => this.onKey(e));
      document.addEventListener('click', (e) => {
        const d = this.querySelector('[data-gmu-sort][open]');
        if (d && !d.contains(e.target)) d.removeAttribute('open');
      });
      MQ.addEventListener('change', () => {
        if (!MQ.matches) this.setDrawer(false);
        this.syncBtn();
      });
      window.addEventListener('pagehide', () => this.save());

      try { if (localStorage.getItem(SIDE_KEY) === '1') this.classList.remove('no-side'); } catch (e) {}
      this.classList.add('is-js');
      this.syncBtn();
      this.restore().then(() => this.observe());
    }

    onClick(e) {
      if (e.target.closest('[data-gmu-toggle]')) {
        if (MQ.matches) { this.setDrawer(true); return; }
        const hidden = this.classList.toggle('no-side');
        try { localStorage.setItem(SIDE_KEY, hidden ? '0' : '1'); } catch (err) {}
        this.syncBtn();
        return;
      }
      if (e.target.closest('[data-gmu-close]')) { this.setDrawer(false); return; }
      const more = e.target.closest('a[data-gmu-more]');
      if (more) { e.preventDefault(); this.more(); return; }
      const link = e.target.closest('a[data-gmu-link]');
      if (link) {
        e.preventDefault();
        const d = link.closest('[data-gmu-sort]');
        if (d) d.removeAttribute('open');
        // Pill / Clear all remove the focused link itself: send focus to the result count instead.
        this.load(link.href, !!link.closest('.gmu-pills, .gmu-side__head, .gmu-col__empty'));
        return;
      }
      if (e.target.closest('[data-gmu-grid] a')) this.save();
    }

    onKey(e) {
      if (e.key === 'Escape') {
        const d = this.querySelector('[data-gmu-sort][open]');
        if (d) { d.removeAttribute('open'); d.querySelector('summary').focus(); return; }
        if (this.classList.contains('is-drawer')) this.setDrawer(false);
        return;
      }
      if (e.key !== 'Tab' || !this.classList.contains('is-drawer')) return;
      const f = [...this.side.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),summary')]
        .filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    setDrawer(on) {
      if (on === this.classList.contains('is-drawer')) return;
      this.classList.toggle('is-drawer', on);
      document.documentElement.classList.toggle('gmu-lock', on);
      this.syncBtn();
      if (on) {
        this.side.setAttribute('role', 'dialog');
        this.side.setAttribute('aria-modal', 'true');
        this.side.setAttribute('aria-labelledby', 'GmuSideT-' + this.sid);
      } else {
        ['role', 'aria-modal', 'aria-labelledby'].forEach((a) => this.side.removeAttribute(a));
      }
      if (on) {
        const x = this.side.querySelector('[data-gmu-close]');
        if (x) x.focus();
      } else if (MQ.matches) {
        this.toggleBtn.focus();
      }
    }

    syncBtn() {
      const open = MQ.matches ? this.classList.contains('is-drawer') : !this.classList.contains('no-side');
      this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    applyForm() {
      const form = this.querySelector('[data-gmu-form]');
      const p = new URLSearchParams();
      for (const [k, v] of new FormData(form)) {
        if (String(v).trim() !== '') p.append(k, v);
      }
      this.load(this.dataset.base + '?' + p.toString());
    }

    async fetchDoc(href) {
      const res = await fetch(withSection(href, this.sid), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('GMU collection: ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      doc.querySelectorAll('a[href*="section_id="]').forEach((a) => a.setAttribute('href', clean(a.href)));
      return doc;
    }

    async load(href, focusCount) {
      const my = ++this.seq;
      this.setAttribute('aria-busy', 'true');
      let doc;
      try { doc = await this.fetchDoc(href); }
      catch (err) { location.href = clean(href); return; }
      if (my !== this.seq) return;

      const open = {};
      this.querySelectorAll('details[data-fg]').forEach((d) => { open[d.dataset.fg] = d.open; });
      const act = document.activeElement;
      const focusId = act && this.contains(act) ? act.id : '';
      const body = this.querySelector('[data-part="side"]');
      const sideTop = body ? body.scrollTop : 0;

      this.querySelectorAll('[data-part]').forEach((el) => {
        const n = doc.querySelector('[data-part="' + el.dataset.part + '"]');
        if (n) el.innerHTML = n.innerHTML;
      });
      if (window.gmuFav) window.gmuFav.syncAll();

      this.querySelectorAll('details[data-fg]').forEach((d) => {
        if (d.dataset.fg in open) d.open = open[d.dataset.fg];
      });
      if (body) body.scrollTop = sideTop;
      if (focusCount) {
        const c = this.querySelector('.gmu-col__count');
        (c && c.offsetParent !== null ? c : this.toggleBtn).focus({ preventScroll: true });
      } else if (focusId) {
        const el = document.getElementById(focusId);
        if (el) el.focus({ preventScroll: true });
      }

      this.pages = 1;
      this.moreP = null;
      history.replaceState({ gmu: { pages: 1, y: window.scrollY } }, '', clean(href));
      this.removeAttribute('aria-busy');

      // Keep the top of the results in view after a filter change further down the page.
      const res = this.querySelector('[data-part="results"]');
      const bar = this.querySelector('.gmu-col__bar');
      if (res && bar) {
        const limit = bar.getBoundingClientRect().bottom;
        if (res.getBoundingClientRect().top < limit) {
          window.scrollTo(0, window.scrollY + res.getBoundingClientRect().top - limit - 20);
        }
      }
      this.observe();
    }

    more() {
      if (this.moreP) return this.moreP;
      const a = this.querySelector('a[data-gmu-more]');
      if (!a) return Promise.resolve(false);
      const wrap = a.closest('[data-gmu-more-wrap]');
      const loading = wrap.querySelector('.gmu-loading');
      if (loading) loading.hidden = false;
      a.hidden = true;
      const my = this.seq;
      this.moreP = this.fetchDoc(a.href).then((doc) => {
        if (my !== this.seq) return false;
        const grid = this.querySelector('[data-gmu-grid]');
        const items = doc.querySelectorAll('[data-gmu-grid] > li');
        items.forEach((li) => grid.appendChild(document.importNode(li, true)));
        if (window.gmuFav) window.gmuFav.syncAll();
        const nw = doc.querySelector('[data-gmu-more-wrap]');
        wrap.innerHTML = nw ? nw.innerHTML : '';
        this.pages += 1;
        return true;
      }).catch(() => {
        if (loading) loading.hidden = true;
        a.hidden = false;
        return false;
      }).finally(() => {
        this.moreP = null;
        this.observe();
      });
      return this.moreP;
    }

    observe() {
      if (this.io) this.io.disconnect();
      const a = this.querySelector('a[data-gmu-more]');
      if (!a || this.restoring) return;
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) this.more();
      }, { rootMargin: '0px 0px 800px 0px' });
      this.io.observe(a.closest('[data-gmu-more-wrap]') || a);
    }

    save() {
      const st = Object.assign({}, history.state || {}, { gmu: { pages: this.pages, y: window.scrollY } });
      try { history.replaceState(st, '', location.href); } catch (e) {}
    }

    async restore() {
      const st = history.state && history.state.gmu;
      if (!st || !(st.pages > 1)) return;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      this.restoring = true;
      while (this.pages < st.pages) {
        const ok = await this.more();
        if (!ok) break;
      }
      this.restoring = false;
      window.scrollTo(0, st.y || 0);
    }
  }

  customElements.define('gmu-collection', GmuCollection);
})();
